import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    DialogRoot,
    DialogTrigger,
    Heading,
    LabeledContainer,
    LabeledContainerActionableTitle,
    LabeledContainerBody,
    LabeledContainerHeader,
    Link,
    RevealableToolbar,
    RevealableToolbarItem,
    RevealableToolbarRoot,
    TextSkeleton,
    Tooltip,
} from '@gitbook/spine-react';
import type { MandateProps } from '@gitbook/util-ts';

import { apiQuery } from '../../state';
import { ConversationsIssuesClusterDialog } from '../Conversations';
import { useChangeRequestsFilters } from './useChangeRequestsFilters';

export type ChangeRequestReviewersBadgesProps = {
    changeRequest: MandateProps<api.ChangeRequest, 'conversationsIssuesCluster'>;
    space: api.Space;
};

/**
 * Display a badge about the topic of the change request.
 */
export function ChangeRequestTopicLink(props: ChangeRequestReviewersBadgesProps) {
    const { changeRequest, space } = props;

    return (
        <LabeledContainer level={2}>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <Heading>Topic</Heading>
                </LabeledContainerActionableTitle>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                <React.Suspense fallback={<TextSkeleton />}>
                    <ConversationsIssuesClusterLink
                        organizationId={space.organization}
                        conversationsIssuesCluster={changeRequest.conversationsIssuesCluster}
                    />
                </React.Suspense>
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

function ConversationsIssuesClusterLink(props: {
    organizationId: string;
    conversationsIssuesCluster: string;
}) {
    const { organizationId, conversationsIssuesCluster } = props;

    const { filters, onFiltersChange } = useChangeRequestsFilters();
    const { data: cluster } = useSuspenseQuery(
        apiQuery(
            'getConversationsIssuesCluster',
            {
                organizationId,
                clusterId: conversationsIssuesCluster,
            },
            {
                nullOnError: true,
            }
        )
    );

    if (!cluster) {
        return (
            <Tooltip label="The conversations context has been manually deleted.">
                <span className="text-muted text-ui-small underline decoration-dotted">
                    Deleted
                </span>
            </Tooltip>
        );
    }

    return (
        <RevealableToolbarRoot>
            <DialogRoot>
                <DialogTrigger>
                    <Link>
                        <Tooltip label="This change request has been created by GitBook Agent in relation to this topic. Click to inspect the related conversations.">
                            <span>{cluster.label}</span>
                        </Tooltip>
                    </Link>
                </DialogTrigger>
                <ConversationsIssuesClusterDialog
                    organizationId={organizationId}
                    clusterId={cluster.id}
                />
            </DialogRoot>
            <RevealableToolbar aria-label="Filter change requests by this topic">
                <RevealableToolbarItem>
                    <Button
                        kind="blank"
                        size="small"
                        label="Filter change requests by this context"
                        leadingIcon={Icon.Filter}
                        active={filters.conversationsIssuesCluster === cluster.id}
                        onPress={() => {
                            onFiltersChange({
                                ...filters,
                                conversationsIssuesCluster:
                                    filters.conversationsIssuesCluster === cluster.id
                                        ? undefined
                                        : cluster.id,
                            });
                        }}
                        iconOnly
                    />
                </RevealableToolbarItem>
            </RevealableToolbar>
        </RevealableToolbarRoot>
    );
}
